import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../api/axios';

const AuthContext = createContext(null);

// Session duration: 30 minutes in milliseconds
const SESSION_DURATION = 30 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [token, setToken] = useState(() => {
    return localStorage.getItem('token') || null;
  });
  const [loginTimestamp, setLoginTimestamp] = useState(() => {
    const saved = localStorage.getItem('loginTimestamp');
    return saved ? parseInt(saved, 10) : null;
  });
  const [isLoading, setIsLoading] = useState(false);
  const sessionCheckInterval = useRef(null);

  // Logout function using useCallback to maintain stable reference
  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setLoginTimestamp(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('loginTimestamp');
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current);
      sessionCheckInterval.current = null;
    }
  }, []);

  // Check if session has expired
  const isSessionExpired = useCallback(() => {
    if (!loginTimestamp) return true;
    return Date.now() - loginTimestamp > SESSION_DURATION;
  }, [loginTimestamp]);

  // Session expiry check - runs on mount and periodically
  useEffect(() => {
    const checkSession = () => {
      if (token && loginTimestamp && isSessionExpired()) {
        console.log('Session expired, logging out...');
        logout();
        // Redirect to login page
        window.location.href = '/login';
      }
    };

    // Check immediately on mount
    checkSession();

    // Check every minute while logged in
    if (token) {
      sessionCheckInterval.current = setInterval(checkSession, 60 * 1000);
    }

    return () => {
      if (sessionCheckInterval.current) {
        clearInterval(sessionCheckInterval.current);
      }
    };
  }, [token, loginTimestamp, isSessionExpired, logout]);

  // Attach JWT to all axios requests
  useEffect(() => {
    const requestInterceptor = axiosInstance.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Cleanup interceptor on unmount
    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor);
    };
  }, [token]);

  // Handle 401 responses (unauthorized)
  useEffect(() => {
    const responseInterceptor = axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && token) {
          // Only auto-logout on 401 if we actually have a token
          // This means the token is invalid/expired
          console.log('Token expired or invalid, logging out...');
          logout();
          // Redirect to login page
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
  }, [logout, token]);

  const login = async (email, password) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post('/auth/login', {
        email,
        password
      });

      const { token: jwtToken, userId, email: userEmail, role } = response.data;

      const userData = {
        userId,
        email: userEmail || email,
        role: role || 'ROLE_USER'
      };

      // Store token, user, and login timestamp in memory and localStorage
      const now = Date.now();
      setToken(jwtToken);
      setUser(userData);
      setLoginTimestamp(now);
      localStorage.setItem('token', jwtToken);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('loginTimestamp', now.toString());

      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Login failed';
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const isAdmin = () => {
    return !!user && user.role === 'ROLE_ADMIN';
  };

  const value = {
    user,
    token,
    isLoading,
    login,
    logout,
    isAuthenticated,
    isAdmin
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
